/**
 * Skill tool processor for the Now Assist Skill Plugin.
 * Handles processing of Skill-as-Tool tools defined in NowAssistSkillConfig.
 */

import { TOOL_TYPES, RESOURCE_ATTRIBUTE_VALUE_SCRIPT } from '../defaults'
import type { Context, ObjectShape, Record as BuildRecord } from '@servicenow/sdk-build-core'
import { applyDefaults, buildDependsOnList, createDependencyEdges } from '../skill-plugin-helpers'
import { NowIdShape } from '@servicenow/sdk-build-plugins'
import type { ToolDefinition } from '../tool-types'
import {
    SYS_ONE_EXTEND_RESOURCE_MAPPING,
    SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
    getResourceCapability,
    getApplicabilityProperties,
    generateInputAttributeValueScript,
} from '../tool-utils'

/**
 * Process a Skill-as-Tool and create the necessary records
 *
 * Skill tools create:
 * 1. sys_one_extend_resource_mapping - The main tool record
 * 2. sys_one_extend_resource_attribute_mapping - Input attribute mappings
 * 3. sys_one_extend_resource_attribute_mapping - Output attribute mappings (custom + default Output)
 * 4. sys_one_extend_resource_edge - Dependency edges (if depends is specified)
 */
export async function processSkillTool(
    tool: ToolDefinition,
    capabilityRecord: BuildRecord,
    toolRecordsMap: Map<string, BuildRecord>,
    arg: ObjectShape,
    factory: Context['factory'],
    diagnostics: Context['diagnostics'],
    decisionToolNames?: Set<string>
): Promise<BuildRecord[]> {
    const records: BuildRecord[] = []

    // Validate tool type
    if (tool.type !== TOOL_TYPES.SKILL) {
        diagnostics.error(arg, `Expected skill tool but got ${tool.type}`)
        return records
    }

    // Validate required fields
    if (!tool.name) {
        diagnostics.error(arg, 'Skill tool requires a name')
        return records
    }

    // Warn user that the referenced skill must be published for skill-as-tool to work
    diagnostics.warn(
        arg,
        'Skills used as tools must be published to work at runtime. Publish the referenced skill from the NASK UI .'
    )

    // Build depends_on_list from dependency names
    const dependsOnList = buildDependsOnList(tool, toolRecordsMap, arg, diagnostics, decisionToolNames)

    // Build metadata for skill tool.
    // The skillId is primarily conveyed through resource_capability (pointing to the
    // target skill's sys_one_extend_capability record), matching platform behavior.
    // We also store toolType in metadata for Fluent reverse detection (transform),
    // since the target skill's capability record may not be present in this project's XML.
    const metadata: Record<string, unknown> = {
        toolType: TOOL_TYPES.SKILL,
    }

    // Determine resource_capability value
    // If skillIdIsReference is true, we create a NowIdShape with the derived capability explicitId.
    // The build system will resolve Now.ID['skill_name__capability'] to the actual sys_id.
    let resourceCapabilityShape: NowIdShape | undefined
    if (tool.skillIdIsReference && tool.skillIdExplicitId) {
        // Create a NowIdShape for the target skill's capability record
        // This will be resolved by the build system to the actual sys_id
        resourceCapabilityShape = new NowIdShape({
            source: tool._source!,
            id: tool.skillIdExplicitId,
        })
    }

    // 1. Create sys_one_extend_resource_mapping record
    const resourceMappingRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_RESOURCE_MAPPING,
        ...(tool.$id ? { explicitId: tool.$id } : {}),
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_MAPPING, $),
            ...getApplicabilityProperties(tool.condition, $),
            depends_on_list: $.val(dependsOnList),
            metadata: $.val(JSON.stringify(metadata)),
            parent_capability: $.val(capabilityRecord.getId()),
            resource_capability: resourceCapabilityShape
                ? $.val(resourceCapabilityShape)
                : $.val(getResourceCapability(tool.type, tool.skillId)),
            resource_name: $.val(tool.name),
        })),
    })
    records.push(resourceMappingRecord)

    // Store in map for dependency resolution
    toolRecordsMap.set(tool.name, resourceMappingRecord)

    // 2. Create sys_one_extend_resource_attribute_mapping for inputs
    // Input definitionAttributeId is the sys_id of the target skill's definition attribute — passed through directly
    if (tool.inputs) {
        for (const input of tool.inputs) {
            const isScripted = input.valueType === 'script'
            const inputScript = isScripted
                ? input.scriptValue!
                : generateInputAttributeValueScript(input.name, input.value)

            const inputAttributeRecord = await factory.createRecord({
                source: tool._source!,
                table: SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
                properties: arg.transform(({ $ }) => ({
                    ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING, $),
                    attribute_value_script: $.val(inputScript).toCdata(),
                    attribute_value_template: $.val(input.value),
                    attribute_value_type: $.val(isScripted ? 'script' : 'template'),
                    resource_attribute_name: $.val(input.name),
                    resource_mapping: $.val(resourceMappingRecord.getId()),
                    type: $.val('input'),
                })),
            })
            records.push(inputAttributeRecord)
        }
    }

    // 3. Create sys_one_extend_resource_attribute_mapping for all outputs (fixed + custom)
    // Each output's definitionAttributeId is the sys_id of the target skill's definition attribute — passed through directly
    if (tool.skillOutputs) {
        for (const [_outputKey, output] of Object.entries(tool.skillOutputs)) {
            const outputTruncate = output.truncate ?? false
            const outputAttributeRecord = await factory.createRecord({
                source: tool._source!,
                table: SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
                properties: arg.transform(({ $ }) => ({
                    ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING, $),
                    attribute_value_script: $.val(RESOURCE_ATTRIBUTE_VALUE_SCRIPT).toCdata(),
                    resource_attribute_name: $.val(output.definitionAttributeId),
                    resource_mapping: $.val(resourceMappingRecord.getId()),
                    truncate: $.val(outputTruncate),
                    type: $.val('output'),
                })),
            })
            records.push(outputAttributeRecord)
        }
    }

    // 4. Create sys_one_extend_resource_edge records for dependencies
    const edgeRecords = await createDependencyEdges(
        tool,
        toolRecordsMap,
        resourceMappingRecord,
        arg,
        factory,
        true,
        tool._source!
    )
    records.push(...edgeRecords)

    return records
}
